/**
 * GuitarDrillCore — shared drill loop for all guitar rung routes.
 *
 * Handles the playing → awaiting → graded/reveal → done phase cycle.
 * The "idle" (setup) phase is owned by the caller via `renderSetupScreen`.
 * When phase reaches "done", a summary is shown; "Again →" restarts the
 * loop; "← Guitar Hub" navigates to /guitar.
 *
 * The exercise logic and grading are unchanged from PR-0→PR-4 foundations.
 */

import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Fretboard, type LitPosition, type FretWindow } from "@music-app/ui-components";
import {
  STANDARD_TUNING,
  midiAt,
  pitchFromMidi,
  pitchToSolfege,
  type FretPosition,
} from "@music-app/theory-engine";
import {
  generate,
  gradeFretboard,
  AudioPlanBuilder,
  type Exercise,
  type FretboardGrading,
} from "@music-app/exercise-generator";
import {
  exerciseReviewSource,
  type Item,
  type Rating,
  type ExerciseSkillId,
} from "@music-app/learning-system";
import {
  useCardsStore,
  usePrefsStore,
  recordReview as dbRecordReview,
  upsertDrillStats,
  getDrillStats,
  type DrillStatsRecord,
} from "@music-app/persistence";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import { ModeSwitcherBar, MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { buttonClasses } from "../components/Button.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface GuitarDrillCoreProps {
  skillId: ExerciseSkillId;
  /** Pre-filtered items to draw rounds from. Must be non-empty. */
  nodeItems: Item[];
  /** The route path for this rung, e.g. "/drill/fret-intervals". */
  exercisePath: string;
  /** Display title shown above the board. */
  drillTitle: string;
  mode: Exclude<ExerciseMode, "challenge">;
  /** When navigated from the plan for a specific node, passed to useListenSession. */
  nodeId?: string;
  from?: string;
  coached?: boolean;
  sessionLength: number;
  /**
   * When true, shows the G–B tuning-offset contextual interstitial during
   * Listen mode on fret-intervals-gb-offset rounds. Set by FretIntervalsExercise
   * when the node has never been drilled (needs-listen status).
   */
  showGbInterstitial?: boolean;
  /**
   * Renders the setup / config screen that appears before the session starts
   * (and after the user stops mid-session). Receives `onStart` (must be called
   * from a user gesture to satisfy the Web Audio autoplay policy) and `ready`
   * (whether samples are loaded — disable the Start button until true).
   */
  renderSetupScreen: (onStart: () => void, ready: boolean) => ReactNode;
  /**
   * Overrides how each round is built. Defaults to picking a random item from
   * `nodeItems` and generating it. A rung with config beyond an item subset
   * (e.g. scale type + octaves selected in the setup screen) supplies its own
   * builder that owns selection and stamps the FSRS item id. Must return a
   * fully-built round; the core plays and grades it unchanged.
   */
  roundBuilder?: () => Round;
}

// ─── Solfège helper ────────────────────────────────────────────────────────────

function solfegeFor(midi: number, tonicMidi: number): string {
  return pitchToSolfege(pitchFromMidi(midi, false), pitchFromMidi(tonicMidi, false), false);
}

const samePos = (a: FretPosition, b: FretPosition): boolean =>
  a.stringIndex === b.stringIndex && a.fret === b.fret;

// ─── State machine ─────────────────────────────────────────────────────────────

type Phase = "idle" | "playing" | "thinking" | "awaiting" | "reveal" | "graded" | "done";

export interface Round {
  exercise: Exercise;
  nodeId: string;
  tonicMidi: number | null;
}

interface DrillState {
  phase: Phase;
  round: Round | null;
  roundNumber: number;
  correctCount: number;
  selected: FretPosition[];
  grading: FretboardGrading | null;
}

type Action =
  | { type: "START"; round: Round }
  | { type: "PLAYBACK_DONE" }
  | { type: "THINKING" }
  | { type: "REVEAL" }
  | { type: "TAP"; pos: FretPosition }
  | { type: "SUBMIT"; taps: FretPosition[]; grading: FretboardGrading }
  | { type: "NEXT"; round: Round }
  | { type: "DONE" }
  | { type: "STOP" };

const INITIAL: DrillState = {
  phase: "idle",
  round: null,
  roundNumber: 0,
  correctCount: 0,
  selected: [],
  grading: null,
};

function reducer(state: DrillState, action: Action): DrillState {
  switch (action.type) {
    case "START":
      return { ...INITIAL, phase: "playing", round: action.round, roundNumber: 1 };
    case "PLAYBACK_DONE":
      return state.phase === "playing" ? { ...state, phase: "awaiting" } : state;
    case "THINKING":
      return state.phase === "playing" ? { ...state, phase: "thinking" } : state;
    case "REVEAL":
      return state.phase === "playing" || state.phase === "thinking"
        ? { ...state, phase: "reveal" }
        : state;
    case "TAP": {
      if (state.phase !== "awaiting") return state;
      const exists = state.selected.some((p) => samePos(p, action.pos));
      const selected = exists
        ? state.selected.filter((p) => !samePos(p, action.pos))
        : [...state.selected, action.pos];
      return { ...state, selected };
    }
    case "SUBMIT":
      if (state.phase !== "awaiting") return state;
      return {
        ...state,
        phase: "graded",
        selected: action.taps,
        grading: action.grading,
        correctCount: state.correctCount + (action.grading.correct ? 1 : 0),
      };
    case "NEXT":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        selected: [],
        grading: null,
      };
    case "DONE":
      return { ...state, phase: "done" };
    case "STOP":
      return { ...INITIAL };
  }
}

// ─── GuitarDrillCore ──────────────────────────────────────────────────────────

export function GuitarDrillCore({
  skillId,
  nodeItems,
  exercisePath,
  drillTitle,
  mode,
  nodeId,
  from,
  coached = false,
  sessionLength,
  showGbInterstitial = false,
  renderSetupScreen,
  roundBuilder,
}: GuitarDrillCoreProps): ReactNode {
  const listenMode = mode === "listen";
  const practiceMode = mode === "practice";

  const [state, dispatch] = useReducer(reducer, INITIAL);
  const [audioReady, setAudioReady] = useState(false);
  const playerRef = useRef<AudioPlayer | null>(null);
  const responseStartRef = useRef<number | null>(null);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);
  const listenAutoAdvance = usePrefsStore((s) => s.listenAutoAdvance);

  const { recordPlay } = useListenSession(skillId, listenMode, nodeId);
  const { recordItem } = useExerciseSession(skillId, practiceMode ? "practice" : "drill", !listenMode);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    return () => {
      player.stop();
      player.destroy();
    };
  }, []);

  // Preload samples on mount so the first Start doesn't block.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    let cancelled = false;
    setAudioReady(false);
    void (async () => {
      try {
        await player.init();
        if (!player.isInstrumentLoaded(instrument)) await player.loadInstrument(instrument);
        player.setInstrument(instrument);
      } catch {
        // Degrade to synth voice.
      } finally {
        if (!cancelled) setAudioReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [instrument]);

  const buildRound = useCallback((): Round => {
    if (roundBuilder) return roundBuilder();
    const item = nodeItems[Math.floor(Math.random() * nodeItems.length)]!;
    const exercise = generate(
      { id: item.id, exerciseType: item.exerciseType, params: item.params },
      nextRoundSeed(),
      { cadenceTempo },
    );
    return { exercise, nodeId: item.nodeId, tonicMidi: exercise.meta.tonicMidi ?? null };
  }, [nodeItems, cadenceTempo, roundBuilder]);

  const playRound = useCallback(async (round: Round) => {
    const player = playerRef.current;
    if (!player) return;
    await player.init();
    if (!player.isInstrumentLoaded(instrument)) await player.loadInstrument(instrument);
    player.setInstrument(instrument);
    await player.play(round.exercise.audio);
    if (listenMode) {
      recordPlay(round.exercise.itemId);
      void upsertDrillStats(round.nodeId, "listening");
      dispatch({ type: "THINKING" });
    } else {
      recordItem(round.exercise.itemId);
      dispatch({ type: "PLAYBACK_DONE" });
    }
  }, [instrument, listenMode, recordPlay, recordItem]);

  // Auto-reveal after thinking pause when listenAutoAdvance is on.
  useEffect(() => {
    if (state.phase !== "thinking" || !listenAutoAdvance) return;
    const t = window.setTimeout(() => dispatch({ type: "REVEAL" }), 1500);
    return () => window.clearTimeout(t);
  }, [state.phase, listenAutoAdvance]);

  // Play teachAudio shortly after the reveal appears.
  useEffect(() => {
    if (state.phase !== "reveal") return;
    const round = state.round;
    if (!round?.exercise.meta.teachAudio) return;
    const player = playerRef.current;
    if (!player) return;
    const t = window.setTimeout(() => void player.play(round.exercise.meta.teachAudio!), 600);
    return () => window.clearTimeout(t);
  }, [state.phase, state.round]);

  const start = useCallback(() => {
    const round = buildRound();
    dispatch({ type: "START", round });
    void playRound(round);
  }, [buildRound, playRound]);

  const next = useCallback(() => {
    if (state.roundNumber >= sessionLength) {
      dispatch({ type: "DONE" });
      return;
    }
    const round = buildRound();
    dispatch({ type: "NEXT", round });
    void playRound(round);
  }, [state.roundNumber, sessionLength, buildRound, playRound]);

  // Reset to idle when mode changes so the setup screen is shown fresh.
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current === mode) return;
    prevModeRef.current = mode;
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, [mode]);

  useEffect(() => {
    if (state.phase === "awaiting") responseStartRef.current = Date.now();
  }, [state.phase]);

  const replay = useCallback(() => {
    if (state.round) void playerRef.current?.play(state.round.exercise.audio);
  }, [state.round]);

  // Notes to replay: the exact pitches the exercise sounded when present, so
  // the controls match what was heard. Fall back to the on-neck voicing for
  // exercise types that don't set playbackMidis.
  const replayMidis = useCallback((): number[] | null => {
    const round = state.round;
    if (!round) return null;
    const heard = round.exercise.meta.playbackMidis;
    if (heard && heard.length > 0) return [...heard].sort((a, b) => a - b);
    const fb = round.exercise.meta.fretboard;
    if (!fb) return null;
    return [...new Set([...fb.given, ...fb.solution].map((p) => midiAt(STANDARD_TUNING, p)))].sort((a, b) => a - b);
  }, [state.round]);

  const playArpeggio = useCallback(() => {
    const midis = replayMidis();
    if (!midis) return;
    const builder = new AudioPlanBuilder();
    for (const midi of midis) { builder.addNote(midi, 0.6, 0.2, "melody"); builder.advance(0.3); }
    void playerRef.current?.play(builder.build());
  }, [replayMidis]);

  const playBlock = useCallback(() => {
    const midis = replayMidis();
    if (!midis) return;
    const builder = new AudioPlanBuilder();
    builder.addChord(midis, 1.5, 0.12, "harmony");
    void playerRef.current?.play(builder.build());
  }, [replayMidis]);

  const submit = useCallback(
    (taps: FretPosition[]) => {
      if (!state.round) return;
      const grading = gradeFretboard(state.round.exercise, taps);
      const responseMs = responseStartRef.current ? Date.now() - responseStartRef.current : 8000;
      dispatch({ type: "SUBMIT", taps, grading });

      const correct = grading.correct;
      void upsertDrillStats(state.round.nodeId, "graded", correct);
      const fsrsEnabled = practiceMode;
      void dbRecordReview({
        itemId: state.round.exercise.itemId,
        sessionId: "",
        rating: correct ? "good" : "again",
        correct,
        responseMs,
        chosenAnswerId: null,
        source: exerciseReviewSource({ challengeMode: false, fsrsEnabled }),
      });
      if (fsrsEnabled) {
        const rating: Rating = correct ? (responseMs < 5000 ? "good" : "hard") : "again";
        void applyReview(state.round.exercise.itemId, rating);
      }
      if (!correct && state.round.exercise.meta.teachAudio) {
        const plan = state.round.exercise.meta.teachAudio;
        window.setTimeout(() => void playerRef.current?.play(plan), 600);
      }
    },
    [state.round, practiceMode, applyReview],
  );

  const handleTap = useCallback(
    (pos: FretPosition) => {
      if (state.phase !== "awaiting" || !state.round) return;
      const fb = state.round.exercise.meta.fretboard;
      if (!fb) return;
      if (fb.notesToPlace === 1) submit([pos]);
      else dispatch({ type: "TAP", pos });
    },
    [state.phase, state.round, submit],
  );

  const handleCheck = useCallback(() => { submit(state.selected); }, [submit, state.selected]);

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "done") {
        e.preventDefault();
        replay();
      } else if (state.phase === "thinking" && (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        dispatch({ type: "REVEAL" });
      } else if ((state.phase === "graded" || state.phase === "reveal") && (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, replay, next]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (state.phase === "idle") {
    return <>{renderSetupScreen(start, audioReady)}</>;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    return (
      <DrillSummary
        correctCount={state.correctCount}
        sessionLength={sessionLength}
        listenMode={listenMode}
        from={from}
        onAgain={start}
        onStop={handleStop}
      />
    );
  }

  return (
    <DrillLoop
      state={state}
      mode={mode}
      exercisePath={exercisePath}
      drillTitle={drillTitle}
      from={from}
      sessionLength={sessionLength}
      showGbInterstitial={showGbInterstitial}
      listenAutoAdvance={listenAutoAdvance}
      onTap={handleTap}
      onCheck={handleCheck}
      onReveal={() => dispatch({ type: "REVEAL" })}
      onNext={next}
      onReplay={replay}
      onPlayArpeggio={playArpeggio}
      onPlayBlock={playBlock}
      onStop={handleStop}
    />
  );
}

// ─── DrillLoop ─────────────────────────────────────────────────────────────────

interface LoopProps {
  state: DrillState;
  mode: Exclude<ExerciseMode, "challenge">;
  exercisePath: string;
  drillTitle: string;
  from?: string;
  sessionLength: number;
  showGbInterstitial: boolean;
  listenAutoAdvance: boolean;
  onTap: (pos: FretPosition) => void;
  onCheck: () => void;
  onReveal: () => void;
  onNext: () => void;
  onReplay: () => void;
  onPlayArpeggio: () => void;
  onPlayBlock: () => void;
  onStop: () => void;
}

function DrillLoop({
  state, mode, exercisePath, drillTitle, from, sessionLength, showGbInterstitial,
  listenAutoAdvance, onTap, onCheck, onReveal, onNext, onReplay, onPlayArpeggio, onPlayBlock, onStop,
}: LoopProps): ReactNode {
  const { phase, round, roundNumber, correctCount, selected, grading } = state;
  const listenMode = mode === "listen";
  const practiceMode = mode === "practice";
  const modeColor = MODE_COLOR[mode];

  const fb = round?.exercise.meta.fretboard;
  const tonicMidi = round?.tonicMidi ?? null;
  const isTriad = (fb?.notesToPlace ?? 1) > 1;
  // The Arpeggio / Block controls only make sense when the exercise sounded
  // more than one note. Chord rungs sound 3–4 notes (single- or multi-tap);
  // single-note scale-degree rungs sound one, so the controls are hidden there.
  const replayNoteCount = round
    ? round.exercise.meta.playbackMidis?.length ??
      (fb ? new Set([...fb.given, ...fb.solution].map((p) => midiAt(STANDARD_TUNING, p))).size : 0)
    : 0;
  const canArpeggiate = replayNoteCount > 1;
  // In Listen mode, use the generator's narrow fret window so the shape doesn't
  // dart across the neck. Drill/Practice shows the full neck.
  const listenFretWindow: FretWindow | undefined =
    listenMode && fb?.listenFretWindow ? fb.listenFretWindow : undefined;
  // Show the G–B interstitial banner in Listen mode when the current round is
  // from the G–B offset node and the node hasn't been introduced yet.
  const showGbBanner =
    showGbInterstitial &&
    listenMode &&
    round?.nodeId === "fret-intervals-gb-offset";
  const isPlaying = phase === "playing";
  const isThinking = phase === "thinking";
  const isAwaiting = phase === "awaiting";
  const isGraded = phase === "graded";
  const isReveal = phase === "reveal";
  const answered = isGraded || isReveal || isThinking ? roundNumber : roundNumber - 1;

  // Hold the auto-advance countdown until the reveal (teach) audio has finished,
  // so the next round doesn't start over the tail of this round's final chord.
  // The teach audio starts 600 ms after the reveal phase begins (see the page's
  // reveal effect); mirror that offset here.
  const [revealAudioDone, setRevealAudioDone] = useState(false);
  useEffect(() => {
    if (!isReveal) {
      setRevealAudioDone(false);
      return;
    }
    const teach = round?.exercise.meta.teachAudio;
    if (!teach) {
      // Nothing plays on reveal — let the countdown start right away.
      setRevealAudioDone(true);
      return;
    }
    setRevealAudioDone(false);
    const holdMs = 600 + teach.totalDuration * 1000 + 250;
    const t = window.setTimeout(() => setRevealAudioDone(true), holdMs);
    return () => window.clearTimeout(t);
  }, [isReveal, round]);

  const labelFor = (pos: FretPosition): string | undefined =>
    isTriad || tonicMidi === null ? undefined : solfegeFor(midiAt(STANDARD_TUNING, pos), tonicMidi);

  const lit: LitPosition[] = [];
  if (fb) {
    // Show given dots (root, bass, or non-root chord tones) as soon as audio ends —
    // they serve as a pitch anchor during the thinking pause and remain visible through reveal.
    if (!isPlaying) {
      for (const g of fb.given) lit.push({ pos: g, role: "given", label: labelFor(g) });
    }
    if (isAwaiting) {
      for (const s of selected) lit.push({ pos: s, role: "selected", label: labelFor(s) });
    }
    if (isGraded && grading) {
      selected.forEach((pos, i) => {
        const st = grading.perTap[i];
        const role = st === "correct" ? "correct" : st === "duplicate" ? "duplicate" : "wrong";
        lit.push({ pos, role, label: labelFor(pos) });
      });
      for (const s of fb.solution) {
        if (!selected.some((p) => samePos(p, s))) {
          lit.push({ pos: s, role: "solution", label: labelFor(s) });
        }
      }
    }
    if (isReveal) {
      for (const s of fb.solution) lit.push({ pos: s, role: "correct", label: labelFor(s) });
    }
  }

  const interactive = isAwaiting;
  const keyName = round?.exercise.meta.keyName ?? "";
  const notesToPlace = fb?.notesToPlace ?? 1;
  const canCheck = isTriad && selected.length === notesToPlace;

  const prompt = (() => {
    if (isPlaying) return "Listen…";
    if (isThinking) return listenAutoAdvance ? "Identify what you heard…" : "What did you hear?";
    if (isAwaiting) {
      if (!isTriad) return round?.exercise.prompt ?? "Tap the note you heard above the root.";
      return round?.exercise.prompt ?? "Build the chord you heard on the highlighted strings.";
    }
    if (isReveal) return round?.exercise.meta.explanation ?? "";
    if (isGraded && grading) {
      if (grading.correct) return `Correct — ${round?.exercise.meta.explanation ?? ""}`;
      return `The answer was: ${round?.exercise.meta.explanation ?? grading.explanation}`;
    }
    return "";
  })();

  const boardAriaLabel = isTriad
    ? "Fretboard — build the chord you heard on the highlighted strings"
    : "Fretboard — tap the note you heard above the root";

  const backLabel = from === "today" ? "Today" : from === "learn" ? "Learn" : "Guitar Hub";
  const backHref = from === "today" ? "/" : from === "learn" ? "/learn" : "/guitar";

  return (
    <div className="max-w-3xl mx-auto py-9 px-8">
      <div className="flex items-center gap-3 mb-4">
        <Link
          to={backHref}
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
        >
          ← {backLabel}
        </Link>
      </div>

      <ModeSwitcherBar current={mode} exercisePath={exercisePath} />
      <ExerciseLessonLink nodeIds={round ? [round.nodeId] : []} />

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">{drillTitle}</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${roundNumber} of ${sessionLength}`}>
          {roundNumber} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4">{keyName ? `Key: ${keyName}` : ""}</p>

      <div
        className="w-full h-0.5 bg-ink/10 mb-2"
        role="progressbar"
        aria-valuenow={roundNumber}
        aria-valuemin={1}
        aria-valuemax={sessionLength}
      >
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${(roundNumber / sessionLength) * 100}%`, backgroundColor: modeColor }}
        />
      </div>
      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {listenMode ? (
          <span>{answered} of {sessionLength} listened</span>
        ) : (
          <span>{correctCount} correct of {answered} answered</span>
        )}
      </div>

      <p className="font-serif italic text-xl text-ink mb-5 min-h-[2rem]" aria-live="polite">
        {prompt}
      </p>

      {showGbBanner && (
        <div
          className="border-l-4 pl-4 py-3 mb-4 bg-paper"
          style={{ borderColor: "#3D7A5F" }}
          role="note"
          aria-label="G–B tuning note"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">
            Intervals — G–B strings
          </p>
          <p className="font-serif text-sm text-ink leading-snug">
            You&apos;ve mastered the standard shapes. Watch what happens crossing
            the G and B strings: because they&apos;re tuned a major third apart
            instead of a fourth, <strong>every shape stretches up one fret.</strong>
          </p>
        </div>
      )}

      <div className="border border-ink/10 rounded-lg p-4 bg-paper mb-6">
        {fb ? (
          <Fretboard
            tuning={STANDARD_TUNING}
            fretWindow={listenFretWindow}
            ariaLabel={boardAriaLabel}
            litPositions={lit}
            allowedStrings={fb.allowedStrings}
            onTap={interactive ? onTap : undefined}
            disabled={!interactive}
            noteLabel={isTriad ? "letter" : "solfege"}
          />
        ) : (
          <p className="font-serif italic text-inkFaded">Loading…</p>
        )}
      </div>

      {isGraded && !listenMode && (
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] mb-4" style={{ color: practiceMode ? modeColor : "#9A9390" }}>
          {practiceMode ? "Counts toward Today." : "No schedule changes."}
        </p>
      )}

      <div className="flex gap-3 flex-wrap">
        <button
          type="button"
          onClick={onReplay}
          className="px-4 py-2 text-sm font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
        >
          Replay (R)
        </button>
        {(isGraded || isReveal) && canArpeggiate && (
          <>
            <button
              type="button"
              onClick={onPlayArpeggio}
              className="px-4 py-2 text-sm font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
            >
              Arpeggio
            </button>
            <button
              type="button"
              onClick={onPlayBlock}
              className="px-4 py-2 text-sm font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
            >
              Block chord
            </button>
          </>
        )}
        {isThinking && !listenAutoAdvance && (
          <button
            type="button"
            onClick={onReveal}
            className="px-5 py-2 text-sm font-medium text-paper transition-colors"
            style={{ backgroundColor: modeColor }}
            aria-keyshortcuts="Space Enter"
          >
            Reveal (Space)
          </button>
        )}
        {isAwaiting && isTriad && (
          <button
            type="button"
            onClick={onCheck}
            disabled={!canCheck}
            className="px-5 py-2 text-sm font-medium text-paper transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: modeColor }}
          >
            Check ({selected.length}/{notesToPlace})
          </button>
        )}
        {(isGraded || isReveal) && (
          <button
            type="button"
            onClick={onNext}
            className="px-5 py-2 text-sm font-medium text-paper transition-colors"
            style={{ backgroundColor: modeColor }}
          >
            {roundNumber >= sessionLength ? "Finish →" : "Next →"}
          </button>
        )}
      </div>
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal && revealAudioDone} onAdvance={onNext} />}
    </div>
  );
}

// ─── DrillSummary ─────────────────────────────────────────────────────────────

function DrillSummary({
  correctCount,
  sessionLength,
  listenMode,
  from,
  onAgain,
  onStop,
}: {
  correctCount: number;
  sessionLength: number;
  listenMode: boolean;
  from?: string;
  onAgain: () => void;
  onStop: () => void;
}): ReactNode {
  const backLabel =
    from === "today" ? "← Today" :
    from === "learn" ? "← Learn" :
    "← Guitar Hub";
  const backHref =
    from === "today" ? "/" :
    from === "learn" ? "/learn" :
    "/guitar";

  return (
    <div className="max-w-2xl mx-auto py-12 px-8 text-center">
      <h1 className="font-serif text-4xl font-medium text-ink mb-3">Session complete</h1>
      {!listenMode && (
        <p className="font-serif italic text-xl text-inkSoft mb-8">
          {correctCount} of {sessionLength} correct.
        </p>
      )}
      <div className="flex gap-3 justify-center">
        <button
          type="button"
          onClick={onAgain}
          className="px-5 py-2 text-sm font-medium text-paper bg-ink transition-colors"
        >
          Again →
        </button>
        <Link
          to={backHref}
          onClick={onStop}
          className={buttonClasses("secondary")}
        >
          {backLabel}
        </Link>
      </div>
    </div>
  );
}

// ─── Guitar node stats ────────────────────────────────────────────────────────

interface NodeStatRow { nodeId: string; label: string; }

export function GuitarNodeStats({ rows }: { rows: readonly NodeStatRow[] }): ReactNode {
  const [stats, setStats] = useState<Record<string, DrillStatsRecord | null>>({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    void Promise.all(
      rows.map((r) => getDrillStats(r.nodeId).then((s) => [r.nodeId, s ?? null] as const)),
    ).then((entries) => setStats(Object.fromEntries(entries)));
  // rows is always a module-level const in callers — stable identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAnyData = rows.some((r) => (stats[r.nodeId]?.played ?? 0) > 0);
  if (!hasAnyData) return null;

  const multiRow = rows.length > 1;
  const cols = multiRow
    ? "grid-cols-[1fr_4rem_4rem_4rem_3rem]"
    : "grid-cols-[4rem_4rem_4rem_3rem]";

  return (
    <div className="mb-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mb-3"
        aria-expanded={open}
      >
        History
        <span aria-hidden className="text-[8px]">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="border border-ink/10">
          <div className={`grid ${cols} px-4 py-2 border-b border-ink/10`}>
            {multiRow && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Node</span>}
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Listen</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Practice</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>
          </div>
          {rows.map((row) => {
            const s = stats[row.nodeId];
            const listened = s ? Math.max(0, s.played - s.correct - s.errors) : null;
            const practiced = s ? s.correct + s.errors : null;
            const pct = practiced && practiced > 0 ? Math.round((s!.correct / practiced) * 100) : null;
            return (
              <div key={row.nodeId} className={`grid ${cols} px-4 py-2.5 border-b border-ink/10 last:border-b-0`}>
                {multiRow && <span className="font-mono text-xs text-ink">{row.label}</span>}
                <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{listened ?? "–"}</span>
                <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{practiced !== null ? practiced : "–"}</span>
                <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{practiced && practiced > 0 ? s!.correct : "–"}</span>
                <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{pct !== null ? `${pct}%` : "–"}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
